import { CanActivateFn, Router } from '@angular/router';
import { inject } from '@angular/core';
import { UserStore } from '../app-state/user.store';
import { SubscriptionStore } from '../app-state/subscription.store';
import { PREVISIT_QUESTIONNAIRE_TOKEN_KEY } from './block-auth-when-questionnaire.guard';
import { environment } from '../../environments/environment';
import { RoleService } from '../services/role.service';
import { AuthTeardownService } from '../services/auth/auth-teardown.service';

export const AuthGuard: CanActivateFn = async (route, state) => {
  const userStore = inject(UserStore);
  const subscriptionStore = inject(SubscriptionStore);
  const roleService = inject(RoleService);
  const authTeardown = inject(AuthTeardownService);
  const token = localStorage.getItem('token');
  const router = inject(Router);
  const returnUrl = state.url;

  // If user is in the patient questionnaire flow (has link token) and has no physician login,
  // redirect to questionnaire so they cannot reach HealthNote. Keeps patient form separate.
  const questionnaireToken = sessionStorage.getItem(
    PREVISIT_QUESTIONNAIRE_TOKEN_KEY,
  );
  if (questionnaireToken && !token) {
    return router.createUrlTree(['/patient-questionnaire', questionnaireToken]);
  }

  if (!token) {
    // Can run mid-session (e.g. another tab logged out and cleared the shared
    // storage), so tear down fully rather than clearing only the user.
    authTeardown.clearAuthentication({ isAuthError: true });
    return router.createUrlTree(['/auth/login'], {
      queryParams: {
        returnUrl: encodeURIComponent(returnUrl),
      },
    });
  }

  // If user is still loading (app initialization), wait but with timeout
  if (userStore.isLoading()) {
    // Add a timeout to prevent infinite loading
    return new Promise((resolve) => {
      const timeout = setTimeout(() => {
        // If still loading after 5 seconds, assume initialization failed
        authTeardown.clearAuthentication({ isAuthError: true });
        resolve(
          router.createUrlTree(['/auth/login'], {
            queryParams: {
              returnUrl: encodeURIComponent(returnUrl),
            },
          }),
        );
      }, 5000);

      // Check if loading completes before timeout
      const checkLoading = () => {
        if (userStore.isLoading()) {
          setTimeout(checkLoading, 100);
        } else {
          clearTimeout(timeout);
          resolve(true);
        }
      };
      checkLoading();
    });
  }

  // If there's an error in the user state, redirect to login
  if (userStore.error()) {
    return router.createUrlTree(['/auth/login'], {
      queryParams: {
        returnUrl: encodeURIComponent(returnUrl),
      },
    });
  }

  // Validate token consistency between localStorage and store
  if (userStore.token() !== token) {
    authTeardown.clearAuthentication();
    localStorage.removeItem('token');
    return router.createUrlTree(['/auth/login'], {
      queryParams: {
        returnUrl: encodeURIComponent(returnUrl),
      },
    });
  }

  // If user data is complete and valid, allow access
  if (
    userStore.user() &&
    userStore.organization() &&
    userStore.organizationUser() &&
    userStore.token() &&
    userStore.isLoading() === false &&
    userStore.error() === false
  ) {
    // Match the real path only: a substring check against the full URL would
    // let a query or matrix parameter bypass the gate.
    const path = state.url.split('?')[0].split(';')[0];
    if (path === '/access-fallback') {
      return true;
    }

    // Compliance approval is always evaluated before exposing a billing or
    // entitlement-recovery page. An inactive organization must never be sent
    // to Checkout simply because it is also unpaid.
    if (userStore.organization()?.isActive !== true) {
      return router.createUrlTree(['/access-fallback']);
    }

    // These are authenticated recovery destinations. They deliberately skip
    // only the subscription lookup to prevent redirect loops; the active-org
    // check above still applies. Payment routes are not included: a direct
    // /payment visit must still distinguish unpaid from unknown status.
    if (
      path === '/subscription-required' ||
      path === '/subscription-check-unavailable'
    ) {
      return true;
    }

    // Use TTL-backed cache to avoid calling API on every route change
    const hasActive = await subscriptionStore.ensureFresh();
    if (hasActive === true) {
      return true;
    }
    if (hasActive === null) {
      return router.createUrlTree(['/subscription-check-unavailable']);
    }

    if (!environment.showPaymentFallback) {
      return router.createUrlTree(['/access-fallback']);
    }

    // An unpaid owner may use any payment child route (including the
    // Checkout return); members get a terminal membership message instead
    // of falling through to the owner-only route guard's generic forbidden
    // page.
    if (path === '/payment' || path.startsWith('/payment/')) {
      return roleService.hasRole('organization-owner')
        ? true
        : router.createUrlTree(['/subscription-required']);
    }

    return router.createUrlTree(
      roleService.hasRole('organization-owner')
        ? ['/payment']
        : ['/subscription-required'],
    );
  }

  // If we have a token but no user data, redirect to login
  return router.createUrlTree(['/auth/login'], {
    queryParams: {
      returnUrl: encodeURIComponent(returnUrl),
    },
  });
};
